import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { ICartEntry } from '@rbi-ctg/menu';
import ModalOrderUnavailable from 'components/modal-order-unavailable';
import { ItemAvailabilityStatus } from 'enums/menu';
import usePosVendor from 'hooks/menu/use-pos-vendor';
import useAddToCartRecentItem from 'hooks/use-add-to-cart-recent-item';
import { useAuthContext } from 'state/auth';
import { useMenuContext } from 'state/menu';
import { useOrderContext } from 'state/order';
import { useStoreContext } from 'state/store';
import { isCartEntryInMenu } from 'utils/cart';
import { maybeMapCartEntryToMenuObjectIdentifier } from 'utils/menu';

import { CarouselContainer } from './carousel-container';
import RecentItemsCarouselItem from './recent-items-carousel-item';
import { Container } from './styled';
import { IRecentItemsProps } from './types';

const RecentItemsCarousel: FC<IRecentItemsProps> = ({ containerHasTopPadding = true }) => {
  const { user } = useAuthContext();
  const { checkItemAvailability } = useMenuContext();
  const { isStoreOpenAndAvailable, prices, store } = useStoreContext();
  const {
    recent: { pendingRecentItem, setPendingRecentItem },
  } = useOrderContext();
  const { addToCart } = useAddToCartRecentItem({ cartEntry: pendingRecentItem });
  const { vendor } = usePosVendor();
  const [selectionUnavailable, setSelectionUnavailable] = useState<ICartEntry | null>(null);

  const uniquePurchasedItems = user?.uniquePurchasedItems;
  const items = useMemo(
    () => (uniquePurchasedItems || []).filter(item => !item.isDonation && isCartEntryInMenu(item)),
    [uniquePurchasedItems]
  );

  const addToCartIfAvailable = useCallback(
    async (cartEntry: ICartEntry) => {
      const itemId = maybeMapCartEntryToMenuObjectIdentifier(cartEntry);
      if (!itemId) {
        setSelectionUnavailable(cartEntry);
        return;
      }
      const { availabilityStatus } = await checkItemAvailability({
        vendor,
        restaurantPosDataId: store.restaurantPosData?._id ?? '',
        storeNumber: store.number ?? '',
        itemId,
      });
      if (availabilityStatus !== ItemAvailabilityStatus.AVAILABLE) {
        setSelectionUnavailable(cartEntry);
      } else {
        addToCart();
      }
    },
    [addToCart, checkItemAvailability, store.number, store.restaurantPosData, vendor]
  );

  useEffect(() => {
    if (isStoreOpenAndAvailable && pendingRecentItem && prices) {
      setPendingRecentItem(null);
      addToCartIfAvailable(pendingRecentItem);
    }
  }, [
    addToCart,
    addToCartIfAvailable,
    isStoreOpenAndAvailable,
    pendingRecentItem,
    prices,
    setPendingRecentItem,
  ]);

  return (
    <Container $containerHasTopPadding={containerHasTopPadding}>
      <CarouselContainer bgColor="transparent">
        {items.map((item, index: number) => (
          <RecentItemsCarouselItem key={index} cartEntry={item} />
        ))}
      </CarouselContainer>
      {selectionUnavailable && (
        <ModalOrderUnavailable
          items={[selectionUnavailable]}
          onDismiss={() => setSelectionUnavailable(null)}
        />
      )}
    </Container>
  );
};

export default RecentItemsCarousel;
